export function buttonToneStyle({ c, dark, tone = "neutral", active = false, disabled = false, compact = false }) {
  const tones = {
    primary: {
      bg: dark ? "#0d2d1a" : "#f0fff4",
      color: dark ? "#48bb78" : "#276749",
      border: dark ? "#48bb78" : "#9ae6b4",
    },
    info: {
      bg: dark ? "#0d2137" : "#ebf8ff",
      color: dark ? "#63b3ed" : "#2b6cb0",
      border: dark ? "#63b3ed" : "#90cdf4",
    },
    warning: {
      bg: dark ? "#2d2a00" : "#fffbea",
      color: dark ? "#f6d860" : "#92610a",
      border: dark ? "#f6d860" : "#f6d860",
    },
    danger: {
      bg: dark ? "#2d1515" : "#fff0f0",
      color: dark ? "#fc8181" : "#cc3333",
      border: dark ? "#4a2020" : "#ffcccc",
    },
    neutral: {
      bg: active ? c.bgSecondary : c.bg,
      color: active ? c.text : c.textSecondary,
      border: c.border,
    },
  };
  const selected = tones[tone] || tones.neutral;

  return {
    background: selected.bg,
    color: selected.color,
    border: `1px solid ${selected.border}`,
    borderRadius: compact ? "6px" : "8px",
    cursor: disabled ? "not-allowed" : "pointer",
    padding: compact ? "4px 8px" : "6px 10px",
    fontSize: compact ? 11 : 12,
    fontWeight: active || tone !== "neutral" ? 600 : 500,
    opacity: disabled ? 0.45 : 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    lineHeight: 1.2,
  };
}

export function AppButton({ c, dark, tone = "neutral", active, compact, disabled, children, style, ...props }) {
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        ...buttonToneStyle({ c, dark, tone, active, disabled, compact }),
        ...style,
      }}
      {...props}
    >
      {children}
    </button>
  );
}

export function IconButton({ c, dark, label, children, tone = "neutral", disabled, style, ...props }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      style={{
        ...buttonToneStyle({ c, dark, tone, disabled, compact: true }),
        minWidth: 28,
        minHeight: 28,
        padding: "4px 7px",
        ...style,
      }}
      {...props}
    >
      {children}
    </button>
  );
}

const iconStyle = {
  width: 15,
  height: 15,
  display: "block",
  flexShrink: 0,
};

export const UiIcon = {
  Add: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" style={iconStyle} aria-hidden="true" {...props}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  ),
  ArrowUp: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" style={iconStyle} aria-hidden="true" {...props}>
      <path d="M12 19V5" />
      <path d="m6 11 6-6 6 6" />
    </svg>
  ),
  ArrowDown: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" style={iconStyle} aria-hidden="true" {...props}>
      <path d="M12 5v14" />
      <path d="m18 13-6 6-6-6" />
    </svg>
  ),
  Edit: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle} aria-hidden="true" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
    </svg>
  ),
  Trash: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle} aria-hidden="true" {...props}>
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="m19 6-.8 14H5.8L5 6" />
      <path d="M10 11v5" />
      <path d="M14 11v5" />
    </svg>
  ),
};

export function TextPanel({ c, label, children, style }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", minHeight: 0, ...style }}>
      {label && (
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: c.textSecondary, marginBottom: 6 }}>
          {label}
        </div>
      )}
      <div
        style={{
          background: c.bgSecondary,
          border: `1px solid ${c.border}`,
          borderRadius: 8,
          padding: 10,
          minHeight: 0,
        }}
      >
        {children}
      </div>
    </section>
  );
}

export function ActionMenu({ c, dark, children, label = "Aktionen" }) {
  return (
    <details style={{ position: "relative" }} onClick={(event) => event.stopPropagation()}>
      <summary
        aria-label={label}
        title={label}
        style={{
          listStyle: "none",
          cursor: "pointer",
          color: c.textSecondary,
          border: `1px solid ${c.border}`,
          borderRadius: 6,
          padding: "3px 8px",
          fontSize: 14,
          lineHeight: 1.2,
          userSelect: "none",
          background: c.bg,
        }}
      >
        ...
      </summary>
      <div
        style={{
          position: "absolute",
          right: 0,
          top: "calc(100% + 4px)",
          zIndex: 20,
          display: "flex",
          flexDirection: "row",
          gap: 4,
          minWidth: 0,
          padding: 6,
          background: c.bg,
          border: `1px solid ${c.border}`,
          borderRadius: 8,
          boxShadow: dark ? "0 12px 30px rgba(0,0,0,.45)" : "0 12px 28px rgba(15,23,42,.14)",
        }}
      >
        {children}
      </div>
    </details>
  );
}
